import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useAreas } from '../hooks/useAreas';
import { usePisos } from '../hooks/usePisos';
import { usePagination } from '../hooks/usePagination';
import { useTenantStore } from '../store/tenantStore';
import QRCode from 'qrcode';

const QRLinks: React.FC = () => {
  const { areas, isLoading, error } = useAreas();
  const { pisos } = usePisos();
  const [filterPisoId, setFilterPisoId] = useState<number | 'todos'>('todos');
  const [searchTerm, setSearchTerm] = useState('');
  // Ya no generamos QR proactivamente; mantendremos un caché opcional si el usuario descarga
  const qrCacheRef = useRef<Map<number, string>>(new Map());
  const [qrError, setQrError] = useState<string | null>(null);

  const baseWeb = window.location.origin;
  const { tenantId } = useTenantStore();
  const filteredItems = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    const filtered = areas.filter(a => {
      const areaPisoId = a.pisoId ?? a.piso?.id;
      const matchesPiso = filterPisoId === 'todos' || areaPisoId === filterPisoId;
      const matchesSearch = term === '' ||
        a.nombre.toLowerCase().includes(term) ||
        (a.piso?.nombre?.toLowerCase().includes(term) ?? false) ||
        a.estado.toLowerCase().includes(term);
      return matchesPiso && matchesSearch;
    });
    return filtered.map(a => ({
      id: a.id,
      nombre: a.nombre,
      url: `${baseWeb}/qr?areaId=${a.id}${tenantId ? `&tenant=${tenantId}` : ''}`,
      estado: a.estado,
      piso: a.piso?.nombre,
    }));
  }, [areas, baseWeb, filterPisoId, tenantId, searchTerm]);

  // Paginación sobre la lista filtrada
  const { page, setPage, totalItems, totalPages, pageStart, pageEnd, pageItems: items, goPrevPage, goNextPage } = usePagination(filteredItems);

  // Resetear a primera página si cambian filtros/búsqueda
  useEffect(() => {
    setPage(0);
  }, [filterPisoId, searchTerm, setPage]);

  // Eliminamos generación proactiva; no hay efecto aquí

  const handleDownload = async (areaId: number, nombre: string, url: string) => {
    try {
      setQrError(null);
      let dataUrl = qrCacheRef.current.get(areaId);
      if (!dataUrl) {
        dataUrl = await QRCode.toDataURL(url, {
          errorCorrectionLevel: 'M',
          margin: 1,
          width: 1024, // alta resolución para impresión/descarga
        });
        qrCacheRef.current.set(areaId, dataUrl);
      }
      const link = document.createElement('a');
      const nombreNormalizado = nombre.replace(/[^a-z0-9_-]/gi, '_');
      link.href = dataUrl;
      link.download = `QR-${nombreNormalizado || areaId}.png`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err) {
      setQrError('Error al generar el código QR. Intenta nuevamente.');
    }
  };

  if (isLoading) {
    return (
      <div className="max-w-7xl mx-auto py-6 px-3">Cargando áreas...</div>
    );
  }

  if (error) {
    return (
      <div className="max-w-7xl mx-auto py-6 px-3 text-red-600">Error al cargar áreas</div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto py-6 px-3 sm:px-6 lg:px-8">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Enlaces QR de Áreas</h1>
        <p className="text-sm text-gray-600">Comparte o abre el enlace externo de cada área para el flujo de Paciente/Personal de Aseo.</p>
      </div>

      <div className="bg-white shadow rounded-lg p-3 sm:p-4 mb-4">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div>
            <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">Piso</label>
            <select
              value={filterPisoId}
              onChange={(e) => setFilterPisoId(e.target.value === 'todos' ? 'todos' : parseInt(e.target.value))}
              className="w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 text-xs sm:text-sm"
            >
              <option value="todos">Todos los pisos</option>
              {pisos.map((p) => (
                <option key={p.id} value={p.id}>{p.nombre}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">Buscar</label>
            <input
              type="text"
              placeholder="Buscar por área, piso, estado..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 text-xs sm:text-sm"
            />
          </div>
          <div className="flex items-end">
            <div className="text-xs sm:text-sm text-gray-600">
              {totalItems} área{totalItems !== 1 ? 's' : ''}
            </div>
          </div>
        </div>
      </div>

      {qrError && (
        <div className="mb-4 bg-red-50 text-red-700 px-4 py-3 rounded">{qrError}</div>
      )}

      <div className="bg-white shadow rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">ID</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">Área</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">Piso</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">Estado</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">URL</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500">Acciones</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {items.map(it => (
                <tr key={it.id}>
                  <td className="px-4 py-3">{it.id}</td>
                  <td className="px-4 py-3">{it.nombre}</td>
                  <td className="px-4 py-3">{it.piso || '-'}</td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-1 rounded-full text-xs ${
                      it.estado === 'Rojo' ? 'bg-red-100 text-red-700' : it.estado === 'Amarillo' ? 'bg-yellow-100 text-yellow-700' : 'bg-green-100 text-green-700'
                    }`}>
                      {it.estado}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <a href={it.url} target="_blank" rel="noreferrer" className="text-blue-600 hover:underline break-all">{it.url}</a>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex justify-end gap-2">
                      <a
                        href={it.url}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex items-center px-3 py-2 bg-indigo-600 text-white text-xs rounded-md hover:bg-indigo-700"
                      >
                        Abrir
                      </a>
                      <button
                        type="button"
                        onClick={() => handleDownload(it.id, it.nombre, it.url)}
                        className="inline-flex items-center px-3 py-2 bg-gray-200 text-gray-800 text-xs rounded-md hover:bg-gray-300"
                      >
                        Descargar
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {items.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-4 py-6 text-center text-gray-500">No hay áreas</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Paginación */}
      {totalItems > 0 && (
        <div className="mt-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="text-xs sm:text-sm text-gray-600">Mostrando {pageStart + 1}–{pageEnd} de {totalItems}</div>
          <div className="flex items-center gap-2">
            <button
              onClick={goPrevPage}
              disabled={page === 0}
              className="px-3 py-2 text-sm rounded-md bg-gray-100 text-gray-700 hover:bg-gray-200 disabled:opacity-40"
              aria-label="Página anterior"
            >
              ← Anterior
            </button>
            <span className="text-xs sm:text-sm text-gray-600">Página {page + 1} de {totalPages}</span>
            <button
              onClick={goNextPage}
              disabled={page >= totalPages - 1}
              className="px-3 py-2 text-sm rounded-md bg-gray-100 text-gray-700 hover:bg-gray-200 disabled:opacity-40"
              aria-label="Página siguiente"
            >
              Siguiente →
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default QRLinks;
